<template>
    <div class="FiltersTopbar">
        <PresetsSearches
            ref="presetsSearches"
            :presetsOptions="presetsOptions"
            @applyPreset="applyPreset"
            @nameEdited="presetNameEdited"
            @refreshPresets="fetchPresets"
        />
        <div
            v-if="isEmpty"
            class="EmptyFilters"
        >
            <WIcon
                icon="info"
                :size="18"
                class="EmptyIcon"
                color="grayLight"
            />
            <WText
                color="gray02"
                weight="bold"
                :size="18"
            >
                {{ $t('filters.emptyRelated.title') }}
            </WText>
            <WText
                color="gray02"
                :size="16"
            >
                {{ $t('filters.emptyRelated.text') }}
            </WText>
        </div>
        <div
            v-else
            class="PinnedFilters"
        >
            <div class="FiltersItems">
                <RelatedFilterItem
                    v-for="(item, index) in filtersItems"
                    :key="index"
                    :item="item"
                    :excluded="excludedEntities.includes(item.entity)"
                    @removeFilter="removeFilter"
                    @pin="pinFilter"
                    @unpin="unpinFilter"
                />
            </div>
            <div class="SaveWrapper">
                <WButton
                    outline
                    color="accent-blue"
                    class="SaveNewPresetButton"
                    iconLeft="heart"
                    :iconSize="14"
                    size="sm"
                    :disabled="isEmpty"
                    @click="createPresetOpen = true"
                >
                    {{ $t('filters.presets.create.action') }}
                </WButton>
            </div>
        </div>
        <div class="RightBoxContainer">
            <div class="SearchResults">
                <WIcon
                    icon="bar-chart-2"
                    :size="16"
                    color="accent-blue"
                />
                <WText
                    color="accent-blue"
                    weight="bold"
                    :size="18"
                    class="TotalResults"
                >
                    {{ totalResults }}
                </WText>
                <WText
                    color="accent-blue"
                    :size="18"
                    class="ResultsLabel"
                >
                    {{ $t('filters.results') }}
                </WText>
            </div>
            <div class="ButtonsContainer">
                <WButton
                    color="accent-blue"
                    class="PinButton"
                    iconLeft="pin"
                    :disabled="!hasUnpined"
                    :iconSize="14"
                    size="sm"
                    @click="pinAllFilters"
                >
                    {{ $t('filters.pinAll') }}
                </WButton>
                <WButton
                    outline
                    color="accent-blue"
                    class="CleanButton"
                    iconLeft="trash-2"
                    :disabled="isEmpty"
                    :iconSize="14"
                    size="sm"
                    @click="cleanAllFilters"
                >
                    {{ $t('filters.cleanAll') }}
                </WButton>
            </div>
        </div>
        <SavePresetModal
            type="save"
            :isOpen="createPresetOpen"
            :presetsOptions="presetsOptions"
            @createPreset="createPreset"
            @updatePreset="updatePreset"
            @close="createPresetOpen = false"
        />
        <RestoreUnpinnedModal
            :isOpen="restoreUnpinnedOpen"
            @close="restoreUnpinnedOpen = false"
            @restore="restoreTemporalUnpinned"
        />
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import {
    difference, findIndex, remove, omit, isEqual,
} from 'lodash'
import { mapGetters, mapActions } from 'vuex'
import { Api, apiRequest } from '@/api'
import { ENTITIES_BY_ID } from '@/entities/entities'
import RelatedFilterItem from './RelatedFilterItem.vue'
import PresetsSearches from './PresetsSearches.vue'
import SavePresetModal from './SavePresetModal.vue'
import RestoreUnpinnedModal from './RestoreUnpinnedModal.vue'

export default {
    name: 'FiltersTopbar',
    components: {
        RelatedFilterItem,
        PresetsSearches,
        SavePresetModal,
        RestoreUnpinnedModal,
    },
    props: {
        entityKey: VueTypes.string,
        filterModel: VueTypes.object,
        totalResults: VueTypes.number,
        excludedEntities: VueTypes.array,
    },
    data () {
        return {
            filtersItems: [],
            presetsOptions: [],
            createPresetOpen: false,
            restoreUnpinnedOpen: false,
        }
    },
    computed: {
        ...mapGetters({
            relatedFilters: 'filters/relatedFilters',
            temporalUnpinned: 'filters/temporalUnpinned',
        }),
        isEmpty () {
            return this.filtersItems.length < 1
        },
        hasUnpined () {
            return this.filtersItems.some(i => !i.pinned)
        },
        entityColor () {
            return ENTITIES_BY_ID[this.entityKey].color
        },
    },
    watch: {
        filterModel (newModel) {
            this.handleFilterModelChange(newModel)
        },
    },
    beforeMount () {
        this.fetchPresets()
        this.initFiltersItems()
    },
    mounted () {
        if (Object.keys(this.temporalUnpinned).length > 0) {
            this.restoreUnpinnedOpen = true
        }
    },
    methods: {
        ...mapActions({
            setRelatedFilters: 'filters/setRelatedFilters',
            setTemporalUnpinned: 'filters/setTemporalUnpinned',
        }),
        setSelectedPreset (value) {
            const { presetsSearches } = this.$refs
            presetsSearches.handlePresetChange(value)
        },
        async fetchPresets () {
            await apiRequest(Api().presets.getAll())
                .then(({ data }) => {
                    this.presetsOptions = data.map(option => ({
                        key: option.id.toString(),
                        label: option.name,
                        data: option.data,
                    }))
                })
        },
        applyPreset (preset) {
            this.setRelatedFilters(preset).then(() => {
                this.$emit('applyPreset')
                this.initFiltersItems()
            })
        },
        createPreset (name) {
            this.pinAllFilters()
            const data = { related: this.relatedFilters }
            apiRequest(Api().presets.create({ name, data })).then((newPreset) => {
                this.fetchPresets().then(() => {
                    this.setSelectedPreset(newPreset.data.id.toString())
                })
            })
        },
        saveTemporalUnpinned () {
            const { filtersItems, setTemporalUnpinned } = this
            const unpinned = {}
            filtersItems.forEach((item) => {
                if (!item.pinned) {
                    unpinned[item.entity] = {
                        ...unpinned[item.entity],
                        [item.field]: item.filtering,
                    }
                }
            })
            setTemporalUnpinned(unpinned)
        },
        restoreTemporalUnpinned () {
            const { relatedFilters, temporalUnpinned } = this
            const relatedCopy = [...relatedFilters]
            Object.keys(temporalUnpinned).forEach((entity) => {
                if (relatedCopy.some(r => r.entity === entity)) {
                    const index = findIndex(relatedCopy, r => r.entity === entity)
                    relatedCopy[index].filters = {
                        ...relatedCopy[index].filters,
                        ...temporalUnpinned[entity],
                    }
                } else {
                    relatedCopy.push({ entity, filters: temporalUnpinned[entity] })
                }
            })
            this.applyPreset(relatedCopy)
            this.setTemporalUnpinned({})
        },
        updatePreset (id) {
            this.pinAllFilters()
            const data = { related: this.relatedFilters }
            apiRequest(Api().presets.updateById(id, { data })).then((newPreset) => {
                this.fetchPresets().then(() => {
                    this.setSelectedPreset(newPreset.data.id.toString())
                })
            })
        },
        presetNameEdited (id) {
            this.fetchPresets().then(() => this.setSelectedPreset(id))
        },
        getFiltersQty ({ conditions }) {
            return conditions ? conditions.length : 1
        },
        initFiltersItems () {
            const items = []
            const { filterModel, relatedFilters, entityKey } = this
            relatedFilters.forEach((related) => {
                const { entity, filters } = related
                Object.keys(filters).forEach((field) => {
                    const filtersQty = this.getFiltersQty(filters[field])
                    items.push({
                        entity,
                        field,
                        filtersQty,
                        pinned: true,
                        filtering: filters[field],
                    })
                })
            })
            Object.keys(filterModel).filter(f => !items.map(i => i.field).includes(f))
                .forEach((field) => {
                    const filtersQty = this.getFiltersQty(filterModel[field])
                    items.push({
                        entity: entityKey,
                        field,
                        filtersQty,
                        pinned: false,
                        filtering: filterModel[field],
                    })
                })
            this.filtersItems = items
        },
        handleFilterModelChange (newModel) {
            const { filtersItems, entityKey, getFiltersQty } = this
            const currentItemsFields = filtersItems.filter(i => i.entity === entityKey).map(f => f.field)
            const removingFields = difference(currentItemsFields, Object.keys(newModel))
            const removingItems = filtersItems.filter(i => removingFields.includes(i.field) && i.entity === entityKey)
            removingItems.forEach(filter => this.removeFilter(filter))
            Object.keys(newModel).forEach((field) => {
                const filterItem = filtersItems.find(f => f.field === field && f.entity === entityKey)
                const filtersQty = getFiltersQty(newModel[field])
                if (filterItem) {
                    const index = filtersItems.indexOf(filterItem)
                    filtersItems.splice(index, 1, {
                        entity: entityKey,
                        field,
                        filtersQty,
                        pinned: !!filterItem?.pinned,
                        filtering: newModel[field],
                    })
                    const hasChanged = !isEqual(newModel[field], filterItem.filtering)
                    const newFilterItem = { field, entity: entityKey, filtering: newModel[field] }
                    if (hasChanged && filterItem?.pinned) this.pinFilter(newFilterItem)
                } else {
                    filtersItems.push({
                        entity: entityKey,
                        field,
                        filtersQty,
                        pinned: false,
                        filtering: newModel[field],
                    })
                }
            })
        },
        pinFilter ({ entity, field, filtering }) {
            const newRelatedFilters = [...this.relatedFilters]
            const prevRelated = newRelatedFilters.find(e => e.entity === entity)
            if (prevRelated) {
                const index = newRelatedFilters.indexOf(prevRelated)
                newRelatedFilters.splice(index, 1, {
                    ...prevRelated,
                    filters: {
                        ...prevRelated.filters,
                        [field]: filtering,
                    },
                })
            } else {
                newRelatedFilters.push({
                    entity,
                    filters: { [field]: filtering },
                })
            }
            this.setRelatedFilters(newRelatedFilters)
            const index = findIndex(this.filtersItems, f => f.field === field && f.entity === entity)
            this.filtersItems[index].pinned = true
            this.setSelectedPreset('')
        },
        unpinFilter ({ entity, field }) {
            const newRelatedFilters = [...this.relatedFilters]
            const prevRelated = newRelatedFilters.find(e => e.entity === entity)
            const shouldRemoveEntity = Object.keys(prevRelated.filters).length === 1
            if (shouldRemoveEntity) {
                remove(newRelatedFilters, r => r.entity === entity)
            } else {
                const index = newRelatedFilters.indexOf(prevRelated)
                newRelatedFilters.splice(index, 1, {
                    ...prevRelated,
                    filters: omit(prevRelated.filters, field),
                })
            }
            this.setRelatedFilters(newRelatedFilters)
            const index = findIndex(this.filtersItems, f => f.field === field && f.entity === entity)
            this.filtersItems[index].pinned = false
            this.setSelectedPreset('')
        },
        pinAllFilters () {
            this.filtersItems.filter(i => !i.pinned).forEach((filter) => {
                this.pinFilter(filter)
            })
            this.setSelectedPreset('')
        },
        removeFilter (filter) {
            const { field, entity } = filter
            const index = findIndex(this.filtersItems, f => f.field === field && f.entity === entity)
            if (this.filtersItems[index].pinned) this.unpinFilter(filter)
            this.filtersItems.splice(index, 1)
            this.$emit('remove', field, entity)
            this.setSelectedPreset('')
        },
        cleanAllFilters () {
            this.filtersItems = []
            this.setRelatedFilters([])
            this.$emit('cleanAll')
            this.setSelectedPreset('')
        },
    },
}
</script>

<style lang="scss" scoped>
.FiltersTopbar {
    display: flex;
    margin: 10px 0;
    height: 102px;
    justify-content: space-evenly;
    border-radius: $border-radius;
    background-color: white;
    .RightBoxContainer {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: space-evenly;
        padding-left: 15px;
        margin: 15px 15px 15px 0;
        border-left: 1px solid $gray-04;
        .SearchResults {
            display: flex;
            align-items: center;
            justify-content: center;
            .TotalResults {
                margin: 4px 4px 0 8px;
            }
            .ResultsLabel {
                margin-top: 4px;
            }
        }
        .ButtonsContainer {
            display: flex;
            align-items: center;
            gap: 10px;
        }
    }
    .PinnedFilters {
        flex: 1;
        display: flex;
        flex-direction: column;
        .FiltersItems {
            display: flex;
            align-content: flex-start;
            flex-wrap: wrap;
            overflow-y: auto;
            gap: 10px;
            padding: 15px 15px 0 15px;
        }
        .SaveWrapper {
            padding: 10px 15px 15px 15px;
            padding-top: 10px;
            width: 100%;
        }
    }
    .EmptyFilters {
        display: flex;
        flex: 1;
        flex-direction: column;
        text-align: center;
        justify-content: center;
        align-items: center;
        .EmptyIcon {
            margin-bottom: 5px;
        }
    }
}
</style>
